import {
  flexRender,
  Row,
  SortingState,
  Table as TableHook,
} from '@tanstack/react-table';
import { Fragment, ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import StyledTableHeader from '../styled/StyledTableHeader';
import clsx from 'clsx';
import { useTranslation } from '@/i18n';

export interface DataTableProps<TData> {
  table: TableHook<TData>;
  onRowClick?: (row: Row<TData>) => void;
  expandedNode?: (row: Row<TData>) => ReactNode;
  sorting?: SortingState;
  /** While true, show a loading row instead of the empty ("no results") state. */
  isLoading?: boolean;
}

const DataTable = <TData,>({
  table,
  onRowClick,
  expandedNode,
  sorting,
  isLoading = false,
}: DataTableProps<TData>) => {
  'use no memo';

  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-gray-100">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-gray-25">
              {headerGroup.headers.map((header) => {
                const columnTitle = header.column.columnDef.header as string;
                return (
                  <TableHead key={header.id} className="text-gray-500">
                    {header.isPlaceholder ? null : header.column.columnDef
                        .id === 'actions' ||
                      header.column.columnDef.id === 'select' ||
                      header.column.columnDef.enableSorting === false ||
                      !sorting ? (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    ) : (
                      <StyledTableHeader
                        column={header.column}
                        title={columnTitle}
                      />
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, index) => (
              <Fragment key={row.id}>
                <TableRow
                  data-state={row.getIsSelected() && 'selected'}
                  className={clsx({
                    'bg-[#F1F3FD]': row.getIsExpanded(),
                    'bg-brand-50': row.getIsSelected() && !row.getIsExpanded(),
                    'bg-white':
                      !row.getIsExpanded() &&
                      !row.getIsSelected() &&
                      index % 2 === 0,
                    'bg-gray-25':
                      !row.getIsExpanded() &&
                      !row.getIsSelected() &&
                      index % 2 === 1,
                  })}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isClickable =
                      onRowClick &&
                      cell.column.columnDef.id !== 'actions' &&
                      cell.column.columnDef.id !== 'select';
                    return (
                      <TableCell
                        key={cell.id}
                        className={clsx(
                          'text-sm leading-6 font-medium text-gray-700',
                          { 'cursor-pointer': isClickable },
                        )}
                        onClick={
                          isClickable ? () => onRowClick(row) : undefined
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                {row.getIsExpanded() && (
                  <tr>
                    <td colSpan={row.getAllCells().length}>
                      {expandedNode ? expandedNode(row) : null}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-gray-500"
              >
                {isLoading ? t('common.loading') : t('common.noResults')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default DataTable;
